// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useRef, useState } from 'react';

import type { BendTypeName } from '@evolvemep/api-ts';
import { Flex } from '@mantine/core';
import { useResizeObserver } from '@mantine/hooks';

import { isNil, isNotNil } from 'helpers/isNotNil';
import { BendLayoutGeometry } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendGeometries/BendLayoutGeometry';
import type { BendPreviewSpecifications } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewGeometry';
import {
  BendPreviewRenderer,
  type RenderMode,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewRenderer';
import { getBendPreview } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/getBendPreview';
import { useBendPreviewContext } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendPreviewContext';

export type BendRenderAreaProps = {
  /** The type of bend to render in the preview. */
  bendType: BendTypeName;
  /** The bend specifications (dimensions, angle, etc.) to use for rendering. */
  bendPreviewData: BendPreviewSpecifications | null;
  /** The mode in which to render the bend preview (e.g., 3D or 2D). */
  renderMode: RenderMode;
  /**
   * When true, shows the bend labels.
   * @defaultValue true
   */
  showLabels?: boolean;
  /**
   * When true, shows the bend layout (flat tube with marks) instead of bent shape.
   * @defaultValue false
   */
  showLayout?: boolean;
  /**
   * Initially autorotates the bend preview.
   * @defaultValue true
   */
  autoRotate?: boolean;
  /**
   * The horizontal margin in pixels to use when auto-fitting the bend preview.
   * @defaultValue 100
   */
  xMargin?: number;
  /**
   * The vertical margin in pixels to use when auto-fitting the bend preview.
   * @defaultValue 100
   */
  yMargin?: number;
};

export const BendRenderArea = ({
  bendType,
  bendPreviewData,
  renderMode,
  showLabels = true,
  showLayout = false,
  autoRotate = true,
  xMargin = 100,
  yMargin = 100,
}: BendRenderAreaProps) => {
  const { highlightedObject, setHighlightedObject } = useBendPreviewContext();

  const renderer = useRef<BendPreviewRenderer | null>(null);
  // Set the animation loop.
  useEffect(() => renderer.current?.animate(renderMode), [renderMode]);

  // Update the highlighted object when the context changes
  useEffect(() => {
    renderer.current?.updateHighlightedObject(highlightedObject);
  }, [highlightedObject]);

  // Update geometry when input props change
  useEffect(() => {
    if (isNil(renderer.current) || isNil(bendType)) return undefined;
    const bendGeometry = showLayout ? new BendLayoutGeometry() : getBendPreview(bendType);
    bendGeometry.createBendPreview(bendPreviewData);
    renderer.current.setGeometry(bendGeometry);
    return () => {
      renderer.current?.clearGeometry();
      bendGeometry.dispose();
    };
  }, [bendPreviewData, bendType, showLayout]);

  useEffect(() => {
    renderer.current?.setLabelsVisible(showLabels);
  }, [showLabels]);

  // Clean up the renderer on unmount
  useEffect(
    () => () => {
      renderer.current?.dispose();
      renderer.current = null;
    },
    [],
  );

  const [ref, rect] = useResizeObserver();

  // AutoRotate logic
  const [isAutoRotating, setIsAutoRotating] = useState(autoRotate);
  useEffect(() => {
    setIsAutoRotating(autoRotate);
  }, [autoRotate]);
  useEffect(() => {
    if (isNil(renderer.current)) return undefined;

    const controls = renderer.current.controls;
    controls.autoRotate = isAutoRotating;
    if (!isAutoRotating) return undefined;

    const listener = () => {
      controls.autoRotate = false;
    };
    controls.addEventListener('start', listener);
    return () => controls.removeEventListener('start', listener);
  }, [isAutoRotating]);

  // Resize logic
  useEffect(() => {
    renderer.current?.updateSize(rect.width, rect.height);
  }, [rect.width, rect.height]);

  // Keep the renderer's margins in sync with the component's xMargin and yMargin props.
  useEffect(() => {
    renderer.current?.updateMargins(xMargin, yMargin);
  }, [xMargin, yMargin]);

  return (
    <Flex flex={1} align="center" justify="center" style={{ overflow: 'hidden', cursor: 'crosshair' }} ref={ref}>
      <canvas
        style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }}
        ref={(canvas) => {
          if (isNil(canvas) || isNotNil(renderer.current)) return;
          renderer.current = new BendPreviewRenderer(canvas, setHighlightedObject);
          const { width, height } = canvas.getBoundingClientRect();
          renderer.current.updateSize(width, height);
          renderer.current.updateMargins(xMargin, yMargin);
        }}
      />
    </Flex>
  );
};
